// @file Vue composable for all autoplay related logic
import type { AutoplayDurationType } from '@@/bits/autoplay'
import { AUTOPLAY_DURATION_TYPES, getAutoplayNotificationMessages } from '@@/bits/autoplay'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { PausableTimer } from '@@/bits/pausable_timer'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { Ref } from 'vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

interface AutoplayOptions {
  currentPageIndex: Ref<number | null>
  isLastPage: Ref<boolean>
  autoplayButtonId: string
  pauseOnOverlayOpen?: boolean
  isAnyOtherOverlayOpen: Ref<boolean>
  computeAutoplayDurationInMs: (x: AutoplayDurationType) => number
  // callback functions
  onStart?: () => void
  onPause?: () => void
  onStop?: () => void
  onNext?: () => void
  onRestart?: () => void
}

interface AutoplayReturn {
  autoplayTimer: PausableTimer
  isAutoplayMenuOpen: Ref<boolean>
  isAutoplaying: Ref<boolean>
  isAutoplayPaused: Ref<boolean>
  isAutoplayLooping: Ref<boolean>
  selectedAutoplayDuration: Ref<AutoplayDurationType>
  computedAutoplayDurationInMs: Ref<number>
  onAutoplayLoopingToggle: () => void
  onAutoplayDurationSelected: (durationType: AutoplayDurationType) => void
  toggleAutoplay: () => void
  startAutoplay: () => void
}

export const useAutoplay = ({
  currentPageIndex,
  isLastPage,
  autoplayButtonId,
  pauseOnOverlayOpen = true,
  isAnyOtherOverlayOpen,
  computeAutoplayDurationInMs,
  onStart = () => {},
  onPause = () => {},
  onStop = () => {},
  onNext = () => {},
  onRestart = () => {},
}: AutoplayOptions): AutoplayReturn => {
  const autoplayTimer = new PausableTimer()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const isAutoplayMenuOpen = ref(false)
  const isAutoplaying = ref(false)
  const isAutoplayPaused = ref(false)
  const isAutoplayLooping = ref(false)

  const previousAutoplayDuration = ref<AutoplayDurationType>()
  const selectedAutoplayDuration = ref<AutoplayDurationType>('auto')

  const computedAutoplayDurationInMs = computed(() => {
    // TODO: Fix the typing issue. `selectedAutoplayDuration` type only allows number or 'auto'
    // but we're actually storing numeric string inside. That's why I need to do all these hacks
    // to satisfy the type checker while converting the string to number.
    const numberDuration = Number(selectedAutoplayDuration.value) as AutoplayDurationType
    const duration =
      Number.isNaN(numberDuration) || !AUTOPLAY_DURATION_TYPES.includes(numberDuration) ? 'auto' : numberDuration
    return computeAutoplayDurationInMs(duration)
  })

  const autoplayHandler = (): void => {
    if (!isLastPage.value) {
      onNext()
      return
    }

    if (isAutoplayLooping.value) {
      onRestart()
    } else {
      stopAutoplay()
    }
  }

  const resetAutoplayTimer = (): void => {
    autoplayTimer.setTimeoutDuration(computedAutoplayDurationInMs.value)
    autoplayTimer.setOnComplete(autoplayHandler)
    autoplayTimer.start()
  }

  const startAutoplay = (): void => {
    isAutoplaying.value = true
    resetAutoplayTimer()

    if (!(autoplayTimer.isPaused || autoplayTimer.isResumed)) {
      onStop()
    }
    onStart()
  }

  const stopAutoplay = (): void => {
    isAutoplaying.value = false
    autoplayTimer.stop()
    onStop()
  }

  const toggleAutoplay = (): void => {
    if (isAutoplaying.value) {
      stopAutoplay()
    } else {
      startAutoplay()
    }
  }

  const onAutoplayLoopingToggle = (): void => {
    isAutoplayLooping.value = !isAutoplayLooping.value
  }

  const onAutoplayDurationSelected = (durationType: AutoplayDurationType): void => {
    previousAutoplayDuration.value = selectedAutoplayDuration.value
    selectedAutoplayDuration.value = durationType
    autoplayTimer.setTimeoutDuration(computedAutoplayDurationInMs.value)
  }

  /**
   * This is the "engine" behind autoplay.
   * Since autoplayTimer is not a recurring interval, it ends after progressing a page.
   * We start the timer again if autoplay is still enabled.
   */
  watch(currentPageIndex, () => {
    if (isAutoplaying.value) {
      startAutoplay()
    }
  })

  const onPauseWhenOverlayOpen = (): void => {
    isAutoplaying.value = false
    autoplayTimer.pause()
    isAutoplayPaused.value = true
    onPause()
    previousAutoplayDuration.value = selectedAutoplayDuration.value
  }

  const isAnyOverlayOpen = computed(() => isAnyOtherOverlayOpen.value || isAutoplayMenuOpen.value)
  if (pauseOnOverlayOpen) {
    watch(isAnyOverlayOpen, () => {
      // pause autoplaying when any menu is open
      if (isAnyOverlayOpen.value && isAutoplaying.value) {
        onPauseWhenOverlayOpen()
      } else if (!isAnyOverlayOpen.value && autoplayTimer.isPaused) {
        isAutoplayPaused.value = false

        // resume autoplaying when the menu is closed
        // if user did not change the autoplay duration while the menu is open, resume from where it was paused
        // otherwise, restart autoplaying with the new duration
        if (previousAutoplayDuration.value === selectedAutoplayDuration.value) {
          isAutoplaying.value = true
          autoplayTimer.resume()
          onStart()
        } else {
          startAutoplay()
        }
      }
    })
  }

  const pauseAutoplayForMouseDevices = (event: KeyboardEvent): void => {
    if (device.touchable) {
      return
    }

    const isKeydownOnAutoplayButton =
      (document.activeElement as HTMLElement).id === autoplayButtonId && [' ', 'Enter'].includes(event.key)
    // if user clicks on the autoplay button, we let the button naturally set isAutoplaying as false
    // so we don't need to manually toggle it here
    if (!isAutoplaying.value || isKeydownOnAutoplayButton) {
      return
    }

    stopAutoplay()
    globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: getAutoplayNotificationMessages().stoppedAutoplaying,
      actionText: __('Resume'),
      actionTextActions: [
        () => {
          startAutoplay()
        },
      ],
    })
  }

  onMounted(() => {
    window.addEventListener('keydown', pauseAutoplayForMouseDevices)
  })
  onUnmounted(() => {
    window.removeEventListener('keydown', pauseAutoplayForMouseDevices)
  })

  return {
    // Overlays
    isAutoplayMenuOpen,

    // Autoplay
    autoplayTimer,
    isAutoplaying,
    isAutoplayPaused,
    isAutoplayLooping,
    selectedAutoplayDuration,
    computedAutoplayDurationInMs,

    // Event handlers
    onAutoplayLoopingToggle,
    onAutoplayDurationSelected,
    toggleAutoplay,
    startAutoplay,
  }
}

interface IndicatorAnimationOptions {
  maxScaleValue?: number
  numStepsInScale?: number
  amountToScalePerInterval?: number
}

export const useAutoplayWithIndicatorAnimation = ({
  // Autoplay
  currentPageIndex,
  isLastPage,
  autoplayButtonId,
  isAnyOtherOverlayOpen,
  computeAutoplayDurationInMs,
  onNext,
  onRestart,
  // Animation
  maxScaleValue = 1,
  numStepsInScale = 100,
  amountToScalePerInterval = 0.01,
}: Omit<AutoplayOptions, 'onStart' | 'onPause' | 'onStop'> & IndicatorAnimationOptions): AutoplayReturn & {
  indicatorTransformScaleX: Ref<number>
} => {
  // #region Animates the indicator bar when autoplaying
  const indicatorTransformScaleX = ref(0)
  let indicatorTransformScaleXInterval: ReturnType<typeof setInterval>
  const startIndicatorTransformScaleXAnimation = (computedAutoplayDurationInMs: number): void => {
    stopIndicatorTransformScaleXAnimation()

    indicatorTransformScaleXInterval = setInterval(() => {
      indicatorTransformScaleX.value += amountToScalePerInterval
      if (indicatorTransformScaleX.value >= maxScaleValue) {
        stopIndicatorTransformScaleXAnimation()
      }
    }, computedAutoplayDurationInMs / numStepsInScale)
  }
  const stopIndicatorTransformScaleXAnimation = (): void => {
    clearInterval(indicatorTransformScaleXInterval)
  }
  // #endregion

  const autoplay = useAutoplay({
    currentPageIndex,
    isLastPage,
    autoplayButtonId,
    pauseOnOverlayOpen: true,
    isAnyOtherOverlayOpen,
    computeAutoplayDurationInMs,
    onStart: () => startIndicatorTransformScaleXAnimation(autoplay.computedAutoplayDurationInMs.value),
    onPause: stopIndicatorTransformScaleXAnimation,
    onStop: () => {
      stopIndicatorTransformScaleXAnimation()
      indicatorTransformScaleX.value = 0
    },
    onNext,
    onRestart,
  })

  return {
    ...autoplay,
    indicatorTransformScaleX,
  }
}
